import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  select,
} from 'redux-saga/effects';
import qs from 'qs';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  archivePlaybookInstance,
  completePlaybookInstance,
  fetchAllPlaybookInstances,
  fetchPlaybookInstanceById,
  markInProgressPlaybookInstance,
  updatePlaybookInstance,
  searchPlaybookInstances,
  fetchPlaybookInstanceChangelog,
  exportPlaybookInstances,
  bulkCompletePlaybookInstances,
  bulkArchivePlaybookInstances,
} from '../../routines/playbookInstanceRoutines';
import { fetchAllPlaybookTemplates } from '../../routines/playbookTemplateRoutines';
import {
  BOOLEAN_MAP,
  GLOBAL_DEFAULT_PAGE_SIZE,
} from '../../../common/constants/CommonConstants';
import { convertFilterToVfl } from '../../../common/util/velarisFilterLanguageUtils';
import onAntdTableExport from '../../../common/util/onAntdTableExport';

const PLAYBOOK_INSTANCE_URL = `${envConfig.APIG_URL}playbook/playbook-instances`;
const PM_URL = `${envConfig.APIG_URL}project-mgt/playbook-instances`;

const getPlaybookInstanceApiHelper = (payload = {}) => {
  const {
    page = 1,
    pageSize = GLOBAL_DEFAULT_PAGE_SIZE,
    sortColumn,
    sortDirection,
    query,
    filters,
    sidebarSelection,
    myPlaybookInstance,
  } = payload || {};

  const params = {
    page: page || 1,
    pageSize: pageSize || GLOBAL_DEFAULT_PAGE_SIZE,
    myPlaybookInstance: myPlaybookInstance || false,
  };

  if (filters && filters.length > 0) {
    params.filters = convertFilterToVfl(filters);
  }

  if (sortColumn && sortDirection) {
    params.sortColumn = sortColumn;
    params.sortDirection = sortDirection;
  }

  if (myPlaybookInstance && myPlaybookInstance.length > 0) {
    params.myPlaybookInstance = myPlaybookInstance;
  }

  if (sidebarSelection && sidebarSelection.length > 0) {
    params.sidebarFilter = sidebarSelection;
  }

  if (query && query.length > 0) {
    params.query = query;
  }

  const url = `${PM_URL}?${qs.stringify(params)}`;
  return request(url);
};

export function* exportPlaybookInstanceSaga({ payload }) {
  const { params, columnConfig } = payload;
  const exportPayload = {
    ...params,
    page: 1,
    pageSize: Number.MAX_SAFE_INTEGER,
  };
  const response = yield call(getPlaybookInstanceApiHelper, exportPayload);
  const taskList = response?.data?.playbookInstances || [];
  yield call(onAntdTableExport(taskList || [], columnConfig));
}

export function* fetchAllPlaybookInstancesSaga({ payload }) {
  const { currentPayload: prevPayload } = yield select(
    (state) => state.playbookInstances
  );

  const payloadToUse =
    payload && payload.usePrevPayload ? prevPayload : payload;

  try {
    const response = yield call(getPlaybookInstanceApiHelper, payloadToUse);
    yield put(
      fetchAllPlaybookInstances.success({
        response,
        currentPayload: payloadToUse,
      })
    );
  } catch (error) {
    yield put(fetchAllPlaybookInstances.failure({ error }));
  } finally {
    yield put(fetchAllPlaybookInstances.fulfill());
  }
}

export function* fetchPlaybookInstanceByIdSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}`
    );
    yield put(fetchPlaybookInstanceById.success(response));
  } catch (error) {
    yield put(fetchPlaybookInstanceById.failure({ error }));
  } finally {
    yield put(fetchPlaybookInstanceById.fulfill());
  }
}

export function* fetchPlaybookInstanceChangeLogByIdSaga({ payload }) {
  const { associatedItemId: instanceId } = payload;
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${instanceId}/changelog`
    );
    yield put(fetchPlaybookInstanceChangelog.success(response));
  } catch (error) {
    yield put(fetchPlaybookInstanceChangelog.failure({ error }));
  } finally {
    yield put(fetchPlaybookInstanceChangelog.fulfill());
  }
}

export function* completePlaybookInstanceSaga({ payload }) {
  try {
    const { completePlaybookOnly = BOOLEAN_MAP.FALSE } = payload;
    const response = yield call(
      request,
      `${PM_URL}/${payload.instanceId}/complete`,
      {
        method: 'POST',
        payload: {
          completePlaybookOnly,
        },
      }
    );
    yield put(
      completePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.COMPLETE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(completePlaybookInstance.failure({ error }));
  } finally {
    yield put(completePlaybookInstance.fulfill());
  }
}

export function* bulkCompletePlaybookInstancesSaga({ payload }) {
  try {
    const { onSuccess, ..._payload } = payload;
    const response = yield call(request, `${PM_URL}/complete`, {
      method: 'POST',
      payload: _payload,
    });
    yield put(
      bulkCompletePlaybookInstances.success({
        response,
        successMessage: SUCCESS_MESSAGES.COMPLETE_PLAYBOOK_INSTANCE,
      })
    );
    if (onSuccess) onSuccess();
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(bulkCompletePlaybookInstances.failure({ error }));
  } finally {
    yield put(bulkCompletePlaybookInstances.fulfill());
  }
}

export function* markInProgressPlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}/in-progress`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      markInProgressPlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.MARK_IN_PROGRESS_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(markInProgressPlaybookInstance.failure({ error }));
  } finally {
    yield put(markInProgressPlaybookInstance.fulfill());
  }
}

export function* updatePlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(request, `${PM_URL}/${payload.instanceId}`, {
      method: 'PATCH',
      payload: payload.formData,
    });

    yield put(
      updatePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
  } catch (error) {
    yield put(updatePlaybookInstance.failure({ error }));
  } finally {
    yield put(updatePlaybookInstance.fulfill());
  }
}

export function* archivePlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}/archive`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      archivePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(archivePlaybookInstance.failure({ error }));
  } finally {
    yield put(archivePlaybookInstance.fulfill());
  }
}

export function* fetchActivePlaybookInstancesSaga({ payload }) {
  try {
    const { level, entityId } = payload;
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/search?orderBy=playbookInstanceId&order=desc&level=${level}&parentIdentifier=${entityId}`
    );
    yield put(searchPlaybookInstances.success(response));
  } catch (error) {
    yield put(searchPlaybookInstances.failure({ error }));
  } finally {
    yield put(searchPlaybookInstances.fulfill());
  }
}

export function* bulkArchivePlaybookInstancesSaga({ payload }) {
  try {
    const { onSuccess, ..._payload } = payload;
    const response = yield call(request, `${PM_URL}/archive`, {
      method: 'POST',
      payload: _payload,
    });
    yield put(
      bulkArchivePlaybookInstances.success({
        response,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_PLAYBOOK_INSTANCE,
      })
    );
    if (onSuccess) onSuccess();
    yield put(fetchAllPlaybookInstances.trigger({ usePrevPayload: true }));
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(bulkArchivePlaybookInstances.failure({ error }));
  } finally {
    yield put(bulkArchivePlaybookInstances.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(
      fetchAllPlaybookInstances.TRIGGER,
      fetchAllPlaybookInstancesSaga
    ),
    takeLatest(
      fetchPlaybookInstanceById.TRIGGER,
      fetchPlaybookInstanceByIdSaga
    ),
    takeLatest(
      fetchPlaybookInstanceChangelog.TRIGGER,
      fetchPlaybookInstanceChangeLogByIdSaga
    ),
    takeEvery(completePlaybookInstance.TRIGGER, completePlaybookInstanceSaga),
    takeEvery(
      bulkCompletePlaybookInstances.TRIGGER,
      bulkCompletePlaybookInstancesSaga
    ),
    takeEvery(
      markInProgressPlaybookInstance.TRIGGER,
      markInProgressPlaybookInstanceSaga
    ),
    takeEvery(archivePlaybookInstance.TRIGGER, archivePlaybookInstanceSaga),
    takeEvery(updatePlaybookInstance.TRIGGER, updatePlaybookInstanceSaga),
    takeEvery(
      searchPlaybookInstances.TRIGGER,
      fetchActivePlaybookInstancesSaga
    ),
    takeLatest(exportPlaybookInstances.TRIGGER, exportPlaybookInstanceSaga),
    takeLatest(
      bulkArchivePlaybookInstances.TRIGGER,
      bulkArchivePlaybookInstancesSaga
    ),
  ]);
}
